/**
 * Aktivitäts-Liste der Server-Übersicht aus den Konsolenzeilen (logs/latest.log):
 * Joins, Chat und „bereit". Minecraft (Vanilla/Paper/Forge/NeoForge) schreibt das
 * selbst ins Log; bei Steam-Spielen (ARK) kommen die Zeilen vom RCON-Log-Poller
 * (backend services/rcon-log-poller.js). Rohausgaben bleiben in der Konsole.
 */
export type ActivityKind = 'join' | 'leave' | 'chat' | 'ready' | 'lost';

export interface ActivityEvent {
  time: string;
  kind: ActivityKind;
  name?: string;
  text: string;
  detail?: string;
}

// „[22:08:48]" (Vanilla, ARK-Poller) oder „[14Sept.2026 18:59:32.421]" (Forge/NeoForge, deutsches Datum)
const TIME = String.raw`^\[(?:[^\]]*?\s)?(\d{2}:\d{2}):\d{2}(?:\.\d+)?\]`;
// „ [Server thread/INFO]:" · „ [Server thread/INFO] [net.minecraft.server.MinecraftServer/]:" · „ [ARK-SE]:"
const SOURCE = String.raw`(?:\s+\[[^\]]*\])+:\s+`;

const MC_CHAT = new RegExp(TIME + SOURCE + String.raw`(?:\[Not Secure\]\s+)?<([^>]+)>\s(.*)$`);
const MC_SAY = new RegExp(TIME + SOURCE + String.raw`\[(?:Server|Rcon)\]\s(.*)$`);
const JOIN_LEAVE = new RegExp(TIME + SOURCE + String.raw`(.+?)\s+(joined|left) the game\s*$`);
const MC_DONE = new RegExp(TIME + SOURCE + String.raw`Done \((\d+(?:[.,]\d+)?)s\)!`);
const ARK_CHAT = new RegExp(TIME + String.raw`\s+\[Chat\]\s+(.+?):\s(.*)$`);
const READY = new RegExp(TIME + String.raw`\s+\[GameManager\].*ist bereit`);
const LOST = new RegExp(TIME + String.raw`\s+\[GameManager\].*RCON-Verbindung verloren`);

const startedIn = (raw: string) => {
  const s = Math.round(parseFloat(raw.replace(',', '.')));
  return s >= 60 ? `gestartet in ${Math.floor(s / 60)} min ${s % 60} s` : `gestartet in ${s} s`;
};

/** Neueste zuerst, höchstens `limit` Einträge. */
export function parseActivity(lines: string[], limit = 6): ActivityEvent[] {
  const out: ActivityEvent[] = [];
  for (const raw of lines) {
    const line = String(raw || '');
    let m: RegExpExecArray | null;
    // Chat zuerst: „<Steve> Bob joined the game" ist Chat, kein Join.
    if ((m = MC_CHAT.exec(line))) {
      out.push({ time: m[1]!, kind: 'chat', name: m[2], text: m[3]! });
    } else if ((m = ARK_CHAT.exec(line))) {
      // ARK: „Name (Name): Text" bzw. „SERVER: Text" (Nachrichten aus dem Panel)
      const name = m[2]!.replace(/\s*\([^)]*\)\s*$/, '');
      out.push({ time: m[1]!, kind: 'chat', name: name === 'SERVER' ? 'Server' : name, text: m[3]! });
    } else if ((m = MC_SAY.exec(line))) {
      out.push({ time: m[1]!, kind: 'chat', name: 'Server', text: m[2]! });
    } else if ((m = JOIN_LEAVE.exec(line))) {
      const joined = m[3] === 'joined';
      out.push({ time: m[1]!, kind: joined ? 'join' : 'leave', name: m[2], text: joined ? 'ist beigetreten' : 'hat den Server verlassen' });
    } else if ((m = MC_DONE.exec(line))) {
      out.push({ time: m[1]!, kind: 'ready', text: 'Server bereit', detail: startedIn(m[2]!) });
    } else if ((m = READY.exec(line))) {
      out.push({ time: m[1]!, kind: 'ready', text: 'Server bereit', detail: 'RCON verbunden' });
    } else if ((m = LOST.exec(line))) {
      out.push({ time: m[1]!, kind: 'lost', text: 'RCON-Verbindung verloren' });
    }
  }
  // Gleiches Ereignis zur gleichen Minute nur einmal - dieselbe Zeile kommt teils doppelt an
  // (Verlauf aus latest.log + Live-Socket, nicht immer direkt hintereinander).
  const seen = new Set<string>();
  const unique = out.filter((e) => {
    const key = `${e.time}|${e.kind}|${e.name ?? ''}|${e.text}|${e.detail ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return unique.slice(-limit).reverse();
}
